import React from 'react'
import InputField from 'components/Shared/Fields'
import { Formik } from 'formik'
import { useNewTranslation } from 'hooks'
import Dropdown from 'particles/Dropdown'
import Modal from 'particles/Modal'
import { FormikRadioButtonGroup } from 'particles/RadioButton/Group'
import { FormikTextArea } from 'particles/TextInputs/TextArea'
import { FormikTextField } from 'particles/TextInputs/TextField'
import styled from 'particles/themes/styled'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectConstants } from 'state/constants'
import * as yup from 'yup'
import { paidOutsideApp, updateTotalEarning } from 'state/campaigns'
import MenuItem from 'particles/MenuItem'
import { COUNTRY_ENUM } from 'services/api/constants'
import ReminderAlert from 'particles/Alert/ReminderAlert'

interface AdjustEarningModalProps {
    userName: string
    currencySign: string
    oldEarning: number
    addition_info: string
    custom_reason: string
    earnings_adjustment_source: string
    reason: string
    submissionUuid: string
    campaignUuid: string
    onClose: () => void
    onSubmit: () => void
}

interface ModalValue {
    earning?: number
    additional_info?: string
    reason?: string
    adjustment_source?: string
    custom_reason?: string
}

const ReasonWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[100]};
`

export const AdjustEarningModal = ({
    campaignUuid,
    submissionUuid,
    userName,
    currencySign,
    oldEarning,
    addition_info,
    custom_reason,
    earnings_adjustment_source,
    reason,
    onClose,
    onSubmit,
}: AdjustEarningModalProps) => {
    const [t] = useNewTranslation()
    const { campaign } = useSelector(selectConstants)
    const [showError, setShowError] = useState(false)
    const dispatch = useDispatch()

    const validationSchema = yup.object({
        earning: yup
            .string()
            .nullable()
            .test({
                name: 'not-empty',
                message: () => t('submission.updatePayout.paidOutsideAppModal.totalEarning.errorMessage.empty'),
                test: value => {
                    if (value === 0 || !value) {
                        return false
                    }
                    return true
                },
            }),
        additional_info: yup
            .string()
            .nullable()
            .notRequired()
            .max(
                1000,
                t('submission.updatePayout.paidOutsideAppModal.additonalInformation.errorMessage.over1000characters'),
            ),
        adjustment_source: yup.string().required(t('errorMessage.validation.emptySelection')),
        reason: yup.string().required(t('submission.updatePayout.editEarningModal.reasonForEdit.errorMessage.empty')),
        custom_reason: yup.string().when('reason', {
            is: val => val === 'others',
            then: yup
                .string()
                .required(t('submission.updatePayout.editEarningModal.reasonForEdit.errorMessage.emptyOtherReason'))
                .max(
                    300,
                    t(
                        'submission.updatePayout.paidOutsideAppModal.reasonForPaidOutsideApp.errorMessage.over300chracters',
                    ),
                ),
            otherwise: yup.string().notRequired(),
        }),
    })

    const initialModalValue: ModalValue = {
        earning: oldEarning,
        additional_info: addition_info,
        reason: reason,
        adjustment_source: earnings_adjustment_source,
        custom_reason: custom_reason,
    }

    const reasonOptions =
        campaign?.submission_earnings_adjustment_reasons?.map(reason => ({
            label: reason.label,
            value: reason.id,
        })) || []
    const paidByOptions =
        campaign?.submission_earnings_adjustment_source?.map(source => ({
            label: source.label,
            value: source.id,
        })) || []

    return (
        <Formik initialValues={initialModalValue} onSubmit={() => {}} validationSchema={validationSchema}>
            {formikBag => {
                return (
                    <Modal
                        primaryButtonProps={{
                            label: t('button.confirm'),
                        }}
                        secondaryButtonProps={{
                            label: t('helper.uploadProgress.inProgress.button.cta1'),
                        }}
                        title={t('submission.updatePayout.editEarningModal.title', { userName })}
                        size="m"
                        hasIcon={false}
                        onClose={onClose}
                        onConfirm={() => {
                            setShowError(true)
                            formikBag.validateForm()
                            if (formikBag.isValid && formikBag.dirty) {
                                dispatch(
                                    updateTotalEarning({
                                        adjustment_source: formikBag.values.adjustment_source || '',
                                        campaignUuid,
                                        submissionUuid,
                                        custom_reason: formikBag.values.custom_reason || '',
                                        reason: formikBag.values.reason || '',
                                        total_earnings: formikBag.values.earning || 0,
                                        payout_remark: formikBag.values.additional_info,
                                        cb: refreshList => {
                                            if (refreshList) {
                                                onSubmit()
                                            }
                                            onClose()
                                        },
                                    }),
                                )
                            }
                        }}
                    >
                        <InputField.NumberInputField
                            title={t('submission.updatePayout.paidOutsideAppModal.totalEarning.Title')}
                            subtitle={t('submission.updatePayout.paidOutsideAppModal.totalEarning.subText')}
                            name="earning"
                            currencySign={currencySign}
                            placeholder="0.00"
                            isRequired
                        />
                        <ReasonWrapper>
                            <Dropdown
                                label={t('submission.updatePayout.editEarningModal.reasonForEdit.title')}
                                subtext={t('submission.updatePayout.editEarningModal.reasonForEdit.subText')}
                                placeholder={t('placeholderText.dropdown.requestEditsReason')}
                                isRequired
                                errorMessage={showError ? formikBag.errors.reason : undefined}
                                onChange={async (value?: unknown) => {
                                    await formikBag.setFieldValue('reason', value)
                                    if (value !== 'others') {
                                        formikBag.setFieldValue('custom_reason', '')
                                    }
                                    formikBag.validateField('reason')
                                }}
                                value={formikBag.values.reason}
                            >
                                {reasonOptions.map((option, index) => (
                                    <MenuItem.Text
                                        key={`search-dropdown-${index}`}
                                        text={option.label}
                                        value={option.value}
                                        tabIndex={index}
                                        dataTestId={option.label}
                                    />
                                ))}
                            </Dropdown>
                            {formikBag.values.reason === 'others' && (
                                <FormikTextField
                                    name="custom_reason"
                                    validateByTouch={false}
                                    showError={showError}
                                    maxInputLength={300}
                                    showInputLength
                                    placeholder={t(
                                        'submission.updatePayout.paidOutsideAppModal.paymentMethod.option.others.placeholder',
                                    )}
                                />
                            )}
                        </ReasonWrapper>
                        <FormikRadioButtonGroup
                            title={t('submission.updatePayout.editEarningModal.paidBy.title')}
                            subtitle={t('submission.updatePayout.editEarningModal.paidBy.subtext')}
                            name="adjustment_source"
                            options={paidByOptions}
                            showError={showError}
                            errorMessage={formikBag.errors.adjustment_source}
                            validateByTouch={false}
                            orientation="horizontal"
                            isRequired
                        />
                        <FormikTextArea
                            name="additional_info"
                            label={t('submission.updatePayout.paidOutsideAppModal.additonalInformation.title')}
                            subtext={t('submission.updatePayout.paidOutsideAppModal.additonalInformation.subText')}
                            maxInputLength={1000}
                            showInputLength
                            error={formikBag.errors.additional_info}
                            isError={!!formikBag.errors.additional_info}
                        />
                    </Modal>
                )
            }}
        </Formik>
    )
}

export default AdjustEarningModal

interface PaidOutsideAppModalProps {
    earning: number
    submissionUuid: string
    currencySign: string
    country: COUNTRY_ENUM
    campaignUuid: string
    onClose: () => void
    onSubmit: () => void
}

interface PaidOutsideAppModalValue {
    earning: number
    reason: string
    custom_reason?: string
    payment_method: string
    custom_payment_method?: string
    additional_info?: string
}

export const PaidOutsideAppModal = ({
    earning,
    submissionUuid,
    campaignUuid,
    country,
    currencySign,
    onSubmit,
    onClose,
}: PaidOutsideAppModalProps) => {
    const [t] = useNewTranslation()
    const [showError, setShowError] = useState(false)
    const dispatch = useDispatch()
    const { campaign } = useSelector(selectConstants)

    const initialModalValue: PaidOutsideAppModalValue = {
        earning,
        reason: '',
        payment_method: '',
        custom_payment_method: '',
        custom_reason: '',
        additional_info: '',
    }

    const validationSchema = yup.object({
        earning: yup
            .string()
            .nullable()
            .test({
                name: 'not-empty',
                message: () => t('submission.updatePayout.paidOutsideAppModal.totalEarning.errorMessage.empty'),
                test: value => {
                    if (value === 0 || !value) {
                        return false
                    }
                    return true
                },
            }),
        additional_info: yup
            .string()
            .nullable()
            .notRequired()
            .max(
                1000,
                t('submission.updatePayout.paidOutsideAppModal.additonalInformation.errorMessage.over1000characters'),
            ),
        reason: yup
            .string()
            .nullable()
            .required(t('submission.updatePayout.paidOutsideAppModal.reasonForPaymentOutsideApp.emptyError')),
        custom_reason: yup.string().when('reason', {
            is: val => val === 'others',
            then: yup
                .string()
                .required(t('submission.updatePayout.paidOutsideAppModal.reasonForPaymentOutsideApp.emptyError'))
                .max(
                    300,
                    t(
                        'submission.updatePayout.paidOutsideAppModal.reasonForPaidOutsideApp.errorMessage.over300chracters',
                    ),
                ),
            otherwise: yup.string().notRequired(),
        }),
        payment_method: yup
            .string()
            .nullable()
            .required(t('submission.updatePayout.paidOutsideAppModal.paymentMethod.emptyError')),
        custom_payment_method: yup.string().when('payment_method', {
            is: val => val === 'others',
            then: yup
                .string()
                .required(t('submission.updatePayout.paidOutsideAppModal.paymentMethod.emptyError'))
                .max(
                    300,
                    t('submission.updatePayout.paymentMethod.reasonForPaidOutsideApp.errorMessage.over100chracters'),
                ),
            otherwise: yup.string().notRequired(),
        }),
    })
    const reasonOptions =
        campaign?.submission_paid_outside_app_reasons?.map(reason => ({
            label: reason.label,
            value: reason.id,
        })) || []
    const paymentOptions =
        campaign?.submission_paid_outside_app_payment_methods?.map(source => ({
            label: t(source.label),
            value: source.id,
        })) || []
    return (
        <Formik
            initialValues={initialModalValue}
            onSubmit={() => {}}
            validationSchema={validationSchema}
            validateOnChange={true}
        >
            {formikBag => {
                const isOverLengthErrorReason =
                    formikBag.errors.custom_reason ===
                    t(
                        'submission.updatePayout.paidOutsideAppModal.reasonForPaidOutsideApp.errorMessage.over300chracters',
                    )
                const isOverLengthErrorPaymentMethod =
                    formikBag.errors.custom_payment_method ===
                    t('submission.updatePayout.paymentMethod.reasonForPaidOutsideApp.errorMessage.over100chracters')
                return (
                    <Modal
                        primaryButtonProps={{
                            label: t('button.confirm'),
                        }}
                        secondaryButtonProps={{
                            label: t('helper.uploadProgress.inProgress.button.cta1'),
                        }}
                        title={t('submission.updatePayout.paidOutsideAppModal.title')}
                        subtext={t('submission.updatePayout.paidOutsideAppModal.subText')}
                        size="m"
                        hasIcon={false}
                        onClose={onClose}
                        onConfirm={() => {
                            setShowError(true)
                            formikBag.validateForm()

                            if (formikBag.isValid && formikBag.dirty) {
                                const {
                                    earning,
                                    payment_method,
                                    reason,
                                    additional_info,
                                    custom_payment_method,
                                    custom_reason,
                                } = formikBag.values
                                dispatch(
                                    paidOutsideApp({
                                        campaignUuid,
                                        submissionUuid,
                                        custom_payment_method: custom_payment_method || '',
                                        payment_method: payment_method || '',
                                        custom_reason: custom_reason || '',
                                        reason: reason,
                                        payout_remark: additional_info,
                                        total_earnings: earning,
                                        cb: refreshList => {
                                            if (refreshList) {
                                                onSubmit()
                                            }
                                            onClose()
                                        },
                                    }),
                                )
                            }
                        }}
                    >
                        <ReminderAlert
                            title={t('submission.updatePayout.paidOutsideAppModal.reminder.title')}
                            form="warning"
                            width="100%"
                        />
                        <InputField.NumberInputField
                            title={t('submission.updatePayout.paidOutsideAppModal.totalEarning.Title')}
                            subtitle={t('submission.updatePayout.paidOutsideAppModal.totalEarning.subText')}
                            name="earning"
                            currencySign={currencySign}
                            placeholder="0.00"
                            isRequired
                            isReadOnly
                            country={country}
                        />

                        <ReasonWrapper>
                            <Dropdown
                                label={t('submission.updatePayout.paidOutsideAppModal.reasonForPaidOutsideApp.title')}
                                subtext={t(
                                    'submission.updatePayout.paidOutsideAppModal.reasonForPaidOutsideApp.subText',
                                )}
                                placeholder={t('placeholderText.dropdown.requestEditsReason')}
                                isRequired
                                errorMessage={showError ? formikBag.errors.reason : undefined}
                                onChange={async (value?: unknown) => {
                                    await formikBag.setFieldValue('reason', value)
                                    if (value !== 'others') {
                                        formikBag.setFieldValue('custom_reason', '')
                                    }

                                    formikBag.validateField('reason')
                                }}
                                value={formikBag.values.reason}
                            >
                                {reasonOptions.map((option, index) => (
                                    <MenuItem.Text
                                        key={`search-dropdown-${index}`}
                                        text={t(option.label)}
                                        value={option.value}
                                        tabIndex={index}
                                        dataTestId={option.label}
                                    />
                                ))}
                            </Dropdown>
                            {formikBag.values.reason === 'others' && (
                                <FormikTextField
                                    name="custom_reason"
                                    validateByTouch={false}
                                    showError={isOverLengthErrorReason || showError}
                                    maxInputLength={300}
                                    showInputLength
                                    placeholder={t(
                                        'submission.updatePayout.paidOutsideAppModal.reasonForPaidOutsideApp.othersPlaceholder',
                                    )}
                                />
                            )}
                        </ReasonWrapper>

                        <ReasonWrapper>
                            <Dropdown
                                label={t('submission.updatePayout.paidOutsideAppModal.paymentMethod.title')}
                                subtext={t('submission.updatePayout.paidOutsideAppModal.paymentMethod.subText')}
                                placeholder={t('placeholderText.dropdown.requestEditsReason')}
                                isRequired
                                errorMessage={showError ? formikBag.errors.payment_method : undefined}
                                onChange={async (value?: unknown) => {
                                    await formikBag.setFieldValue('payment_method', value)
                                    if (value !== 'others') {
                                        formikBag.setFieldValue('custom_payment_method', '')
                                    }
                                    formikBag.validateField('payment_method')
                                }}
                                value={formikBag.values.payment_method}
                            >
                                {paymentOptions.map((option, index) => (
                                    <MenuItem.Text
                                        key={`search-dropdown-${index}`}
                                        text={t(option.label)}
                                        value={option.value}
                                        tabIndex={index}
                                        dataTestId={option.label}
                                    />
                                ))}
                            </Dropdown>
                            {formikBag.values.payment_method === 'others' && (
                                <FormikTextField
                                    name="custom_payment_method"
                                    validateByTouch={false}
                                    showError={isOverLengthErrorPaymentMethod || showError}
                                    maxInputLength={300}
                                    showInputLength
                                    placeholder={t(
                                        'submission.updatePayout.paidOutsideAppModal.paymentMethod.othersPlaceholder',
                                    )}
                                />
                            )}
                        </ReasonWrapper>

                        <FormikTextArea
                            name="additional_info"
                            label={t('submission.updatePayout.paidOutsideAppModal.additonalInformation.title')}
                            subtext={t('submission.updatePayout.paidOutsideAppModal.additonalInformation.subText')}
                            maxInputLength={1000}
                            showInputLength
                            error={formikBag.errors.additional_info}
                            isError={!!formikBag.errors.additional_info}
                        />
                    </Modal>
                )
            }}
        </Formik>
    )
}
